import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Mail, ExternalLink, CheckCircle2, ShieldCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OSF_URL = "https://osf.io/4g3a9/overview?view_only=5fc6436bfaf64eb6aad79385d740961a";
const EMAIL = "sumanth.kumbargere@plymouth.ac.uk";

const REASONS = [
  "General enquiry",
  "Feedback on the toolkit",
  "Report a problem with a download",
  "Request to collaborate",
  "Permission to translate or adapt the toolkit",
  "Other",
];

const schema = z.object({
  full_name: z.string().min(1, "Please enter your full name."),
  email: z.string().min(1, "Please enter your email address.").email("Please enter a valid email address."),
  institution: z.string().optional(),
  country: z.string().optional(),
  reason: z.string().min(1, "Please select a reason for contact."),
  message: z.string().min(1, "Please enter a message."),
  consent: z.literal(true, { errorMap: () => ({ message: "Please tick the consent box to continue." }) }),
  company: z.string().optional(), // honeypot
});

const fieldClass =
  "mt-2 w-full rounded-md border border-melt-grey bg-white px-4 py-3 text-base text-melt-indigo placeholder:text-melt-indigo/40 focus:border-melt-teal";
const labelClass = "block text-[15px] font-semibold text-melt-indigo";
const errorClass = "mt-1.5 text-sm font-medium text-[#B42318]";

const ContactForm = () => {
  const mountRef = useRef(Date.now());
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (values) => {
    try {
      const payload = { ...values, elapsed_ms: Date.now() - mountRef.current };
      const res = await axios.post(`${API}/contact`, payload);
      if (res.data?.success) setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please try again shortly.";
      window.alert(detail);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-lg border border-melt-teal/40 bg-melt-teal/5 p-8 text-center" data-testid="contact-success">
        <CheckCircle2 size={40} className="mx-auto text-melt-teal" aria-hidden="true" />
        <h3 className="mt-4 font-serif text-2xl font-semibold text-melt-indigo">Thank you for your message</h3>
        <p className="mx-auto mt-3 max-w-md text-base text-melt-indigo/80">
          Your enquiry has been received. We read every message and will respond as soon as we can. Your
          comments help shape future versions of MELT.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6" data-testid="contact-form">
      {/* Honeypot: hidden from users, catches bots */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="company">Company (leave blank)</label>
        <input id="company" type="text" tabIndex={-1} autoComplete="off" {...register("company")} />
      </div>

      <div>
        <label htmlFor="full_name" className={labelClass}>
          Full name <span className="text-melt-teal">*</span>
        </label>
        <input id="full_name" type="text" className={fieldClass} aria-required="true" aria-invalid={!!errors.full_name} {...register("full_name")} data-testid="input-full-name" />
        {errors.full_name && <p className={errorClass} data-testid="error-full-name">{errors.full_name.message}</p>}
      </div>

      <div>
        <label htmlFor="email" className={labelClass}>
          Email address <span className="text-melt-teal">*</span>
        </label>
        <input id="email" type="email" className={fieldClass} aria-required="true" aria-invalid={!!errors.email} {...register("email")} data-testid="input-email" />
        {errors.email && <p className={errorClass} data-testid="error-email">{errors.email.message}</p>}
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="institution" className={labelClass}>Institution or organisation</label>
          <input id="institution" type="text" className={fieldClass} {...register("institution")} data-testid="input-institution" />
        </div>
        <div>
          <label htmlFor="country" className={labelClass}>Country</label>
          <input id="country" type="text" className={fieldClass} {...register("country")} data-testid="input-country" />
        </div>
      </div>

      <div>
        <label htmlFor="reason" className={labelClass}>
          Reason for contact <span className="text-melt-teal">*</span>
        </label>
        <select id="reason" className={fieldClass} aria-required="true" aria-invalid={!!errors.reason} defaultValue="" {...register("reason")} data-testid="input-reason">
          <option value="" disabled>Please choose…</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        {errors.reason && <p className={errorClass} data-testid="error-reason">{errors.reason.message}</p>}
      </div>

      <div>
        <label htmlFor="message" className={labelClass}>
          Message <span className="text-melt-teal">*</span>
        </label>
        <textarea id="message" rows={6} className={fieldClass} aria-required="true" aria-invalid={!!errors.message} {...register("message")} data-testid="input-message" />
        {errors.message && <p className={errorClass} data-testid="error-message">{errors.message.message}</p>}
      </div>

      <div>
        <label className="flex items-start gap-3 text-[15px] text-melt-indigo/85">
          <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 accent-[#0F7C7B]" aria-invalid={!!errors.consent} {...register("consent")} data-testid="input-consent" />
          <span>
            I have read the{" "}
            <a
              href="/data-protection"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-melt-teal underline underline-offset-2 hover:text-melt-teal-600"
              data-testid="consent-policy-link"
            >
              Data Protection and Cookie Policy
            </a>{" "}
            and I agree that my details may be stored and used solely to respond to this enquiry and
            to inform the development of the toolkit.
          </span>
        </label>
        {errors.consent && <p className={errorClass} data-testid="error-consent">{errors.consent.message}</p>}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="inline-flex items-center justify-center rounded-md bg-melt-teal px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-melt-teal-600 disabled:opacity-60"
        data-testid="submit-button"
      >
        {isSubmitting ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};

export default function Contact() {
  return (
    <>
      <PageHeader
        eyebrow="Contact us"
        title="Contact us"
        intro="MELT is a living resource. We welcome feedback, questions, and examples of how the toolkit has been used in your own research context. Your comments will inform future versions of the guidance materials."
        testId="contact-header"
      />

      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-24">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          {/* Form */}
          <Reveal>
            <div className="relative rounded-xl border border-melt-grey bg-white p-8 md:p-10">
              <h2 className="font-serif text-2xl font-semibold">Send us a message</h2>
              <p className="mt-2 text-base text-melt-indigo/70">Fields marked with an asterisk are required.</p>
              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </Reveal>

          {/* Correspondence */}
          <Reveal delay={0.1}>
            <aside className="space-y-6">
              <div className="rounded-xl border border-melt-grey bg-white p-8" data-testid="correspondence-panel">
                <h2 className="font-serif text-xl font-semibold">Correspondence</h2>
                <address className="mt-4 not-italic text-base leading-relaxed text-melt-indigo/80">
                  Dr. Sumanth Kumbargere Nagraj<br />
                  Peninsula Dental School<br />
                  University of Plymouth<br />
                  Drake Circus, PL4 8AA<br />
                  Plymouth, United Kingdom
                </address>
                <a
                  href={`mailto:${EMAIL}`}
                  className="mt-4 inline-flex items-center gap-2 text-base font-medium text-melt-teal hover:underline"
                  data-testid="correspondence-email"
                >
                  <Mail size={17} aria-hidden="true" />
                  {EMAIL}
                </a>
                <p className="mt-5 border-t border-melt-grey pt-5 text-[15px] text-melt-indigo/75">
                  The toolkit files are also archived on the Open Science Framework.
                </p>
                <a
                  href={OSF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 text-base font-medium text-melt-teal hover:underline"
                  data-testid="correspondence-osf"
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  Open Science Framework
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              </div>

              <div className="flex items-start gap-3 rounded-xl border border-melt-grey woven-texture-light p-6" data-testid="privacy-note">
                <ShieldCheck size={22} className="mt-0.5 shrink-0 text-melt-teal" aria-hidden="true" />
                <p className="text-[15px] leading-relaxed text-melt-indigo/80">
                  Personal details submitted through this form are used only to respond to your enquiry and
                  to inform the development of the toolkit. They are not shared with third parties. You may
                  request deletion of your details at any time by writing to the correspondence address above.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </>
  );
}
